import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import CourseCard from "@/components/CourseCard";
import CTASection from "@/components/CTASection";
import { SCHOOL_CLASSES, BOARDS, SCHOOL_SUBJECTS, HIGHER_SEC_STREAMS, CA_PROGRAM } from "@/constants/site";
import SEO, { breadcrumb, course } from "@/components/SEO";

export default function SchoolCourses() {
  const [activeBoard, setActiveBoard] = useState("SSC");
  const [activeStream, setActiveStream] = useState(0);

  const streamData = HIGHER_SEC_STREAMS[activeStream];

  return (
    <div>
      <SEO
        title="School Coaching Classes 1 to 12 — SSC, CBSE, ICSE | AM Group of Education"
        description="Premium school coaching classes for 1 to 10 (SSC, CBSE, ICSE) and Classes 11 & 12 Science and Commerce streams — plus Chartered Accountancy (CA) coaching."
        path="/school-courses"
        jsonLd={[
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "School Courses", path: "/school-courses" },
          ]),
          course(
            "School Coaching Classes 1–12 (SSC / CBSE / ICSE)",
            "Structured coaching for Classes 1 to 10 across SSC, CBSE and ICSE boards, plus Class 11 & 12 Science and Commerce streams.",
            "/school-courses"
          ),
        ]}
      />
      <PageHeader
        title="School Courses"
        subtitle="Complete school-education coaching from 1st to 12th standard — across SSC, CBSE and ICSE boards. Streams for Science and Commerce, plus professional CA coaching."
        crumb="School Courses"
      />

      {/* Classes 1 – 10 */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">School Education</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight">Classes 1 – 10</h2>
            <p className="mt-3 text-brand-mute">Choose your board — SSC / CBSE / ICSE — and preview every subject we cover. Personalised batches, weekly tests and detailed report cards.</p>
          </div>

          {/* Board selector */}
          <div className="mt-8 flex flex-wrap gap-3" role="tablist" data-testid="board-selector">
            {BOARDS.map((b) => {
              const active = b.code === activeBoard;
              return (
                <button
                  key={b.code}
                  onClick={() => setActiveBoard(b.code)}
                  data-testid={`board-tab-${b.code.toLowerCase()}`}
                  className={`group relative rounded-2xl px-5 py-4 text-left transition-all ${
                    active
                      ? "bg-brand-dark text-white shadow-[0_15px_40px_-15px_rgba(78,52,46,.5)]"
                      : "bg-brand-cream text-brand-dark hover:bg-white border border-brand-line"
                  }`}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-semibold ${active ? "text-brand-gold" : "text-brand-primary"}`}>Board</div>
                  <div className="display-serif text-xl font-bold mt-0.5">{b.name}</div>
                  <div className={`text-xs mt-1 max-w-[220px] ${active ? "text-white/75" : "text-brand-mute"}`}>{b.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Subjects grid for active board */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeBoard}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45 }}
              className="mt-10"
            >
              <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
                <div>
                  <h3 className="display-serif text-2xl sm:text-3xl font-bold text-brand-dark">All Subjects — <span className="text-brand-primary">{activeBoard}</span></h3>
                  <p className="text-sm text-brand-mute mt-1">Available for every standard from 1st to 10th.</p>
                </div>
                <Link
                  to="/contact"
                  data-testid="all-subjects-btn"
                  className="btn-primary !px-6 !py-3 text-xs"
                >
                  All Subjects <i className="fa-solid fa-layer-group" />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {SCHOOL_SUBJECTS.map((s, i) => (
                  <motion.div
                    key={s.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.4 }}
                    whileHover={{ y: -4 }}
                    className="group rounded-2xl border border-brand-line bg-white hover:bg-brand-cream p-4 sm:p-5 transition-all cursor-default"
                    data-testid={`subject-${s.name.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    <span className="w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary grid place-items-center group-hover:bg-brand-primary group-hover:text-brand-gold transition-colors">
                      <i className={`fa-solid fa-${s.icon}`} />
                    </span>
                    <div className="mt-3 font-semibold text-brand-dark text-sm sm:text-base leading-tight">{s.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-mute mt-1">{activeBoard}</div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest text-brand-mute">
                {SCHOOL_CLASSES.map((s) => (
                  <span key={s} className="rounded-full bg-brand-cream border border-brand-line px-3 py-1">{s}</span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Classes 11 & 12 with Streams */}
      <section className="py-16 sm:py-20 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">Higher Secondary</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight">Classes 11 &amp; 12</h2>
            <p className="mt-3 text-brand-mute">Pick a stream — Science or Commerce — and get board-aligned coaching, subject-wise mentors and personalised study plans.</p>
          </div>

          {/* Stream selector */}
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="stream-selector">
            {HIGHER_SEC_STREAMS.map((s, i) => {
              const active = i === activeStream;
              return (
                <button
                  key={s.name}
                  onClick={() => setActiveStream(i)}
                  data-testid={`stream-tab-${s.name.toLowerCase()}`}
                  className={`group relative rounded-3xl p-6 text-left overflow-hidden transition-all ${
                    active
                      ? `bg-gradient-to-br ${s.color} text-white shadow-[0_25px_60px_-25px_rgba(78,52,46,.6)]`
                      : "bg-white border border-brand-line text-brand-dark hover:-translate-y-1"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-14 h-14 rounded-2xl grid place-items-center ${active ? "bg-brand-gold/25 text-brand-gold" : "bg-brand-primary text-brand-gold"}`}>
                      <i className={`fa-solid fa-${s.icon} text-2xl`} />
                    </span>
                    <div>
                      <div className="display-serif text-2xl font-extrabold">{s.name}</div>
                      <div className={`text-[10px] uppercase tracking-widest font-semibold mt-0.5 ${active ? "text-brand-gold" : "text-brand-primary"}`}>{s.tag}</div>
                    </div>
                    <i className={`fa-solid fa-arrow-right ml-auto transition-transform ${active ? "text-brand-gold translate-x-0" : "text-brand-primary -translate-x-2 group-hover:translate-x-0"}`} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Subjects for active stream */}
          <AnimatePresence mode="wait">
            <motion.div
              key={streamData.name}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45 }}
              className="mt-10"
            >
              <h3 className="display-serif text-xl sm:text-2xl font-bold text-brand-dark mb-4">
                {streamData.name} Subjects
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {streamData.subjects.map((sub, i) => (
                  <motion.div
                    key={sub.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl bg-white border border-brand-line p-4 sm:p-5 text-center"
                    data-testid={`${streamData.name.toLowerCase()}-subject-${i}`}
                  >
                    <span className="w-11 h-11 rounded-xl bg-brand-primary/10 text-brand-primary grid place-items-center mx-auto">
                      <i className={`fa-solid fa-${sub.icon}`} />
                    </span>
                    <div className="mt-3 font-semibold text-brand-dark text-sm">{sub.name}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Professional — CA Course */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">Professional Courses</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark">Chartered Accountancy (CA)</h2>
            <p className="mt-3 text-brand-mute">A structured CA pathway with mentors who&apos;ve cleared each stage. From Foundation to Final — with career guidance, mock tests and one-on-one mentorship.</p>
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl bg-gradient-to-br from-brand-dark via-brand-primary to-brand-dark text-white p-8 sm:p-10 overflow-hidden shadow-[0_30px_80px_-30px_rgba(78,52,46,.55)]">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-gold/20 blur-3xl" />
                <div className="relative">
                  <span className="w-16 h-16 rounded-2xl bg-brand-gold/25 text-brand-gold grid place-items-center">
                    <i className={`fa-solid fa-${CA_PROGRAM.icon} text-2xl`} />
                  </span>
                  <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-brand-gold font-semibold">{CA_PROGRAM.tag}</div>
                  <h3 className="display-serif text-3xl sm:text-4xl font-extrabold mt-1">{CA_PROGRAM.name}</h3>
                  <p className="mt-3 text-white/85 leading-relaxed">{CA_PROGRAM.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-primary !bg-brand-gold !text-brand-dark hover:!bg-white">Enquire Now <i className="fa-solid fa-paper-plane" /></Link>
                    <Link to="/contact" className="btn-outline !border-brand-gold !text-brand-gold hover:!bg-brand-gold hover:!text-brand-dark">Learn More <i className="fa-solid fa-arrow-right" /></Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {CA_PROGRAM.stages.map((s, i) => (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border border-brand-line bg-brand-cream p-5"
                  data-testid={`ca-stage-${i}`}
                >
                  <span className="w-11 h-11 rounded-xl bg-brand-primary text-brand-gold grid place-items-center">
                    <i className={`fa-solid fa-${s.icon}`} />
                  </span>
                  <div className="mt-3 font-semibold text-brand-dark">{s.name}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
